import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Database, Lock, Eye, Trash2, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { LogoIcon } from '../components/Logo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <LogoIcon size={8} />
              <span className="font-bold text-xl text-purple-700">Spirify</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-600" />
              {t.privacy?.title || 'Politique de confidentialité'}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              {t.privacy?.lastUpdate || 'Dernière mise à jour'} : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-600 leading-relaxed">
                {t.privacy?.intro || "Chez Spirify, nous accordons une grande importance à la protection de vos données personnelles. Cette politique de confidentialité explique quelles informations nous collectons, comment nous les utilisons et quels sont vos droits."}
              </p>
            </section>

            {/* Données collectées */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-purple-600" />
                {t.privacy?.dataCollected || 'Données collectées'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-gray-600">{t.privacy?.dataCollectedText || "Nous collectons les données suivantes :"}</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                  <li>{t.privacy?.data1 || "Informations de compte : nom d'utilisateur, adresse email"}</li>
                  <li>{t.privacy?.data2 || "Données de progression : leçons complétées, scores, XP"}</li>
                  <li>{t.privacy?.data3 || "Données techniques : type de navigateur, adresse IP (anonymisée)"}</li>
                </ul>
              </div>
            </section>

            {/* Utilisation des données */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-purple-600" />
                {t.privacy?.dataUsage || 'Utilisation des données'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600">{t.privacy?.dataUsageText || "Vos données sont utilisées pour :"}</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2 mt-2">
                  <li>{t.privacy?.usage1 || "Fournir et améliorer nos services"}</li>
                  <li>{t.privacy?.usage2 || "Personnaliser votre expérience d'apprentissage"}</li>
                  <li>{t.privacy?.usage3 || "Vous envoyer des notifications importantes"}</li>
                  <li>{t.privacy?.usage4 || "Établir des statistiques anonymisées"}</li>
                </ul>
              </div>
            </section>

            {/* Protection des données */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-purple-600" />
                {t.privacy?.dataProtection || 'Protection des données'}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacy?.dataProtectionText || "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction. Vos données sont stockées sur des serveurs sécurisés et chiffrées."}
              </p>
            </section>

            {/* Vos droits */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Trash2 className="w-5 h-5 text-purple-600" />
                {t.privacy?.yourRights || 'Vos droits'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-2">{t.privacy?.yourRightsText || "Conformément au RGPD, vous disposez des droits suivants :"}</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                  <li>{t.privacy?.right1 || "Droit d'accès à vos données"}</li>
                  <li>{t.privacy?.right2 || "Droit de rectification"}</li>
                  <li>{t.privacy?.right3 || "Droit à l'effacement (droit à l'oubli)"}</li>
                  <li>{t.privacy?.right4 || "Droit à la portabilité des données"}</li>
                  <li>{t.privacy?.right5 || "Droit d'opposition au traitement"}</li>
                </ul>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h3 className="text-lg font-semibold mb-3">
                🍪 {t.privacy?.cookies || 'Cookies'}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t.privacy?.cookiesText || "Nous utilisons des cookies essentiels pour le fonctionnement du site (authentification, préférences de langue). Aucun cookie publicitaire ou de tracking n'est utilisé."}
              </p>
            </section>

            {/* Contact */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-purple-600" />
                {t.privacy?.contact || 'Contact'}
              </h3>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-3">
                  {t.privacy?.contactText || "Pour exercer vos droits ou pour toute question concernant vos données personnelles, contactez-nous :"}
                </p>
                <div className="space-y-1">
                  <p className="font-semibold">MFP Services France</p>
                  <p>3 rue des Tanneurs</p>
                  <p>13100 Aix-en-Provence, France</p>
                  <p className="mt-2">
                    <a href="mailto:contact@spirify.ai" className="text-purple-600 hover:underline">
                      contact@spirify.ai
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Bouton retour */}
            <div className="pt-4 border-t">
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.privacy?.back || 'Retour'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
